import { MicrophoneIcon, VideoCameraIcon } from "@heroicons/react/solid";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { CheckIcon } from "@radix-ui/react-icons";
import * as Sentry from "@sentry/nextjs";
import { Timer } from "apollo/components/audio-recorder/timer";
import { AnimatePresence, motion } from "motion/react";
import React, { useRef, useState } from "react";
import Webcam from "react-webcam";
import ArrowRight from "ui/components/icons/ArrowRight";
import { Spinner } from "ui/components/icons/Spinner";
import { TrashIcon } from "ui/components/icons/TrashIcon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "ui/components/primitives/dropdown-menu";
import { useSpacebarPress } from "ui/hooks/useSpacebarPress";
import { fn } from "ui/lib/helpers";
import { cn } from "ui/lib/utils";

/**
 * Pick a container/codec pair this browser can actually record. Chrome does
 * VP9/WebM, Safari only does MP4/H.264 — hardcoding the former made
 * `new MediaRecorder(...)` throw NotSupportedError there. Returning undefined
 * lets MediaRecorder fall back to its own default.
 */
const RECORDER_MIME_TYPES = [
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
  "video/mp4;codecs=h264,aac",
  "video/mp4",
];

const pickRecorderMimeType = () => {
  if (typeof MediaRecorder === "undefined") return undefined;
  return RECORDER_MIME_TYPES.find((type) =>
    typeof MediaRecorder.isTypeSupported === "function"
      ? MediaRecorder.isTypeSupported(type)
      : false
  );
};

/** "video/webm;codecs=vp9,opus" -> "webm" */
const extensionForMimeType = (mimeType) => {
  const base = (mimeType || "").split(";")[0].trim().toLowerCase();
  const extensions = {
    "video/webm": "webm",
    "video/mp4": "mp4",
    "video/quicktime": "mov",
    "video/ogg": "ogv",
  };
  return extensions[base] || "webm";
};

const useVideoRecorder = () => {
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [selectedMicrophone, setSelectedMicrophone] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [cameras, setCameras] = useState(null);
  const [microphones, setMicrophones] = useState(null);
  const [status, setStatus] = useState("idle");
  const [mediaBlob, setMediaBlob] = useState(null);
  const [permissionError, setPermissionError] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleWebcamLoaded = () => {
    setLoading(false);
  };

  const getDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === "videoinput");
      const microphones = devices.filter(
        (device) => device.kind === "audioinput"
      );

      return {
        cameras,
        microphones,
        devices,
      };
    } catch (err) {
      console.error("Error fetching devices:", err);
    }
  };

  React.useEffect(() => {
    const requestPermissions = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        const { cameras, microphones } = await getDevices();
        setPermissionError(false);
        setSelectedCamera(cameras[0]?.deviceId || null);
        setSelectedMicrophone(microphones[0]?.deviceId || null);
        setCameras(cameras);
        setMicrophones(microphones);
      } catch (err) {
        setPermissionError(true);
      }
    };

    requestPermissions();
  }, []);

  const startRecording = async () => {
    setStatus("countdown");
    let count = 3;
    setCountdown(count);

    const countdownInterval = setInterval(async () => {
      count--;
      setCountdown(count);
      if (count === 0) {
        clearInterval(countdownInterval);
        setCountdown(null);
        // Status stays "countdown" (record button disabled) until the
        // recorder exists, so a stop press can't land on a null ref.
        let stream = null;
        try {
          const mimeType = pickRecorderMimeType();
          stream = await navigator.mediaDevices.getUserMedia({
            audio: { deviceId: selectedMicrophone },
            video: { deviceId: selectedCamera, aspectRatio: 9 / 16 },
          });
          const recorder = new MediaRecorder(
            stream,
            mimeType ? { mimeType } : undefined
          );

          recorder.addEventListener("dataavailable", (event) => {
            if (event.data.size > 0) {
              setMediaBlob(event.data);
            }
          });

          recorder.start();
          mediaRecorderRef.current = recorder;
          setStatus("recording");
        } catch (err) {
          stream?.getTracks().forEach((track) => track.stop());
          mediaRecorderRef.current = null;
          setStatus("idle");
          Sentry.captureException(err, {
            tags: { location: "apollo-video-recorder-start" },
          });
        }
      }
    }, 1000);
  };

  const stopRecording = () => {
    setStatus("ready");
    mediaRecorderRef.current?.stop();
  };

  const updateStream = async () => {
    // Stop the current stream if it exists
    if (webcamRef.current.stream) {
      webcamRef.current.stream.getTracks().forEach((track) => track.stop());
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: selectedMicrophone },
      video: { deviceId: selectedCamera },
    });

    // Set the new stream to the webcam ref
    webcamRef.current.stream = stream;
  };

  const clearMediaBlob = () => {
    setMediaBlob(null);
  };

  return {
    isRecording: status === "recording",
    status,
    mediaBlob,
    loading,
    updateStream,
    handleWebcamLoaded,
    startRecording,
    stopRecording,
    clearMediaBlob,
    getDevices,
    cameras,
    microphones,
    selectedCamera,
    setSelectedCamera,
    selectedMicrophone,
    setSelectedMicrophone,
    permissionError,
    webcamRef,
    countdown,
  };
};

/**
 * @typedef PlayerProps
 * @type {object}
 * @property {Blob} srcBlob
 *
 * @param {PlayerProps}
 */
function Player({ srcBlob, ...props }) {
  if (!srcBlob) return null;

  return (
    <video src={URL.createObjectURL(srcBlob)} controls autoPlay {...props} />
  );
}

const VideoRecorderContent = ({ handleSave }) => {
  const {
    isRecording,
    loading,
    handleWebcamLoaded,
    mediaBlob,
    updateStream,
    startRecording,
    stopRecording,
    clearMediaBlob,
    setSelectedCamera,
    setSelectedMicrophone,
    cameras,
    microphones,
    selectedCamera,
    selectedMicrophone,
    permissionError,
    webcamRef,
    countdown,
    status,
  } = useVideoRecorder();

  const handleClick = async () => {
    if (mediaBlob || status === "countdown") return;
    if (!isRecording) {
      clearMediaBlob();
      startRecording();
    } else {
      stopRecording();
    }
  };

  useSpacebarPress({
    onKeyPress: handleClick,
  });

  const handleDiscard = async () => {
    clearMediaBlob();
  };

  const onHandleSave = async () => {
    // Label the file with what was actually recorded. This used to hardcode
    // .mp4 / video/mp4 over VP9 WebM bytes, which put a wrong Content-Type on
    // the S3 object; Mux only coped because it probes content rather than
    // trusting the header.
    const type = mediaBlob.type || "video/webm";
    const file = new File(
      [mediaBlob],
      `video-recording.${extensionForMimeType(type)}`,
      { type, lastModified: Date.now() }
    );
    handleSave(file);
  };

  return (
    <div className="relative h-full w-full overflow-hidden">
      <section className="absolute inset-x-0 top-0 z-20 w-full p-6 flex-center">
        <div
          className={cn("text-white px-2 py-1", {
            "bg-black/50": isRecording,
            "opacity-0": !isRecording,
          })}
        >
          {isRecording ? (
            <Timer key={isRecording} isActive={isRecording} />
          ) : (
            "0:00"
          )}
        </div>
      </section>
      <div className="h-full w-full max-w-7xl mx-auto flex-center">
        {countdown !== null && (
          <div className="absolute inset-0 flex-center text-8xl text-white">
            {countdown}
          </div>
        )}
        {permissionError && (
          <div className="flex-col px-12 space-y-4 flex-center absolute h-full w-full p-4 bg-black/20">
            <div className="text-sm text-center text-foreground">
              <span>
                Please ensure Apollo has access to your camera and microphone
              </span>
            </div>
          </div>
        )}
        {loading && (
          <div className="absolute inset-0 flex-center">
            <Spinner className="w-[30px] h-[30px] text-white" />
          </div>
        )}
        {!mediaBlob ? (
          <Webcam
            audio
            muted
            className="w-full max-w-xl h-auto rounded bg-black"
            onUserMedia={handleWebcamLoaded}
            videoConstraints={{
              deviceId: selectedCamera,
              aspectRatio: 9 / 16,
            }}
            audioConstraints={{
              deviceId: selectedMicrophone,
            }}
            ref={webcamRef}
          />
        ) : null}
        <Player
          className="w-full max-w-xl h-auto rounded bg-black"
          srcBlob={mediaBlob}
        />
      </div>
      <section className={cn("z-20 absolute w-full bottom-0 p-2")}>
        <AnimatePresence mode="popLayout">
          {!mediaBlob ? (
            <motion.div
              key="recording-controls"
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 10 }}
              className="flex-center gap-4"
            >
              {!isRecording ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button aria-label="Choose microphone" className="rounded-full bg-secondary border p-2">
                      <MicrophoneIcon className="w-6" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="top">
                    {microphones?.map((microphone) => (
                      <DropdownMenuItem
                        onClick={(e) => {
                          setSelectedMicrophone(microphone.deviceId);
                        }}
                        key={microphone.deviceId}
                        value={microphone.deviceId}
                      >
                        <span>{microphone.label}</span>
                        {selectedMicrophone === microphone.deviceId ? (
                          <CheckIcon className="w-5 h-5 text-primary" />
                        ) : null}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
              <button
                autoFocus
                onClick={handleClick}
                aria-label={isRecording ? "Stop recording" : "Start recording"}
                disabled={status === "countdown"}
                className={cn(
                  "border-4 border-white rounded-full w-16 h-16 flex-center ring-0 focus:ring-0 focus:outline-none transition duration-500 ease-in-out"
                )}
              >
                <div
                  className={cn(
                    "bg-danger transition duration-500 ease-in-out block",
                    {
                      "rounded-full w-12 h-12": !isRecording,
                      "rounded w-8 h-8 scale-90": isRecording,
                      "scale-100": status !== "countdown",
                      "scale-75": status === "countdown",
                    }
                  )}
                />
              </button>
              {!isRecording ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button aria-label="Choose camera" className="rounded-full bg-secondary border p-2">
                      <VideoCameraIcon className="w-6" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="top">
                    {cameras?.map((camera) => (
                      <DropdownMenuItem
                        onClick={(e) => {
                          setSelectedCamera(camera.deviceId);
                        }}
                        key={camera.deviceId}
                        value={camera.deviceId}
                      >
                        <span>{camera.label}</span>
                        {selectedCamera === camera.deviceId ? (
                          <CheckIcon className="w-5 h-5 text-primary" />
                        ) : null}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </section>
      <section className={cn("z-20 absolute w-full top-0 p-2")}>
        <AnimatePresence mode="popLayout">
          {!!mediaBlob ? (
            <motion.div
              key="save-controls"
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 10 }}
              className="w-full space-x-4 flex items-center justify-between"
            >
              <button
                aria-label="Discard recording"
                className="rounded-full bg-secondary border p-2"
                onClick={handleDiscard}
              >
                <TrashIcon className="w-5 text-error" />
              </button>
              <button
                className={cn(
                  "bg-white shadow-md text-black font-bold rounded-full px-4 py-2 flex-center gap-2 group"
                )}
                onClick={onHandleSave}
              >
                <span className="">Next</span>
                <ArrowRight className="w-5 transition-transform group-hover:translate-x-1" />
              </button>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </section>
    </div>
  );
};

export const VideoRecorder = React.memo(
  ({ handleSave = fn, open = false, onOpenChange = fn }) => {
    return (
      <DialogPrimitive.Dialog open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content
            style={{
              maxHeight: 730,
              minHeight: 730,
              width: 420,
              minWidth: 420,
            }}
            // centering stays on `transform` (not translate-x/y utilities) so the
            // animate keyframes replace it under v4 - see LADD-3977 / ui dialog.tsx
            className={
              "fixed overflow-scroll left-[50%] top-[50%] z-50 grid w-full h-full rounded-xl max-w-[90%] max-h-[90%] [transform:translate(-50%,-50%)] gap-4 border bg-popover p-2 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] md:w-full"
            }
          >
            <VideoRecorderContent handleSave={handleSave} />
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Dialog>
    );
  }
);
